import React, { useRef, useState } from 'react'
import { useToast } from '~/lib/ui/toast'
import { apiPost } from '~/util/api'
import { useMutation } from '@tanstack/react-query'
import { Textarea } from '~/lib/ui/textarea'
import { useCandidate, queryClient } from './CandidateProvider'
import { useTranslation } from '~/hooks/useTranslation'
import { Label } from '~/lib/ui/label'
import { Button } from '~/lib/ui/button'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectItem,
  SelectContent,
} from '~/lib/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import PrivacySettings from './PrivacySettings'
import { useResumeReview } from './ResumeReviewDetail'
import Quill from 'quill'
import RichTextEditor from '~/lib/ui/rich-text-editor'

export const DurationSelect = ({ duration, onValueChange }) => {
  const durationOptions = [1, 2, 5, 10, 15, 30, 45, 60, 75, 90, 'Custom']
  const [hasCustomDuration, setHasCustomDuration] = useState(false)
  const handleValueChange = (value) => {
    if (value === 'Custom') {
      setHasCustomDuration(true)
    } else {
      setHasCustomDuration(false)
      onValueChange(value)
    }
  }
  return (
    <div className="flex flex-row justify-evenly">
      <Select
        defaultValue={duration}
        onValueChange={handleValueChange}
        className="w-full"
      >
        <SelectTrigger>
          <SelectValue placeholder="Select Duration" />
        </SelectTrigger>
        <SelectContent>
          {durationOptions.map((option) => (
            <SelectItem key={option} value={option.toString()}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasCustomDuration && (
        <input
          type="number"
          className="ml-2 w-full"
          onChange={(e) => onValueChange(e.target.value)}
          placeholder="Custom Duration"
        />
      )}
    </div>
  )
}

const NoShowDialog = ({ calendlyEvent, children }) => {
  const { candidate, coachId } = useCandidate()
  const candidateId = candidate?.id
  const { candidateFirstName, coachName } = useResumeReview()
  const [open, setOpen] = useState(false)
  const [noteDetails, setNoteDetails] = useState('')
  const [privacySettings, setPrivacySettings] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const editorRef = useRef(null)
  const allPrivacySettings = ['csm', 'coach', 'admin']
  const formPrivacySettings =
    privacySettings.indexOf('all') > -1 ? allPrivacySettings : privacySettings
  const [duration, setDuration] = useState()
  const { toast } = useToast()

  const { t } = useTranslation('NoShowDialog')
  const postNoShow = async (formData) => {
    setIsSaving(true)
    const postPath = `/coach/candidates/${candidateId}/service_request_no_shows.json?message=${encodeURIComponent(noteToCandidate)}`
    const response = await apiPost(postPath, formData, true)
    return response
  }

  const createNoShowMutation = useMutation({
    mutationFn: postNoShow,
    onSuccess: () => {
      setIsSaving(false)
      setOpen(false)
      queryClient.invalidateQueries(['serviceRequests', candidateId])
      toast({
        title: 'Success',
        description: 'No-show entry created successfully',
      })
    },
    onError: (error) => {
      setIsSaving(false)
      console.error('Error creating no-show entry:', error)
      const description =
        error?.response?.data?.error ||
        'An error occurred while uploading the no-show entry.'
      console.log('Error description:', description)
      toast({
        description,
        title: 'Could Not Upload No-Show Entry',
        variant: 'destructive',
        duration: 10_000,
      })
    },
  })

  const saveClick = async () => {
    if (isSaving) return null

    const f = new FormData()
    f.append('service_request_no_show[coach_id]', coachId)
    f.append('service_request_no_show[candidate_id]', candidateId)
    f.append('service_request_no_show[calendly_event_id]', calendlyEvent.id)

    f.append(
      'service_request_no_show[coach_note_attributes][duration]',
      duration,
    )
    f.append(
      'service_request_no_show[coach_note_attributes][topics][Resume][]',
      'No Show',
    )
    formPrivacySettings.forEach((setting) => {
      f.append(
        'service_request_no_show[coach_note_attributes][privacy_settings][]',
        setting,
      )
    })
    f.append(
      'service_request_no_show[coach_note_attributes][note]',
      noteDetails,
    )
    createNoShowMutation.mutate(f)
  }

  // Utility to generate default note text for resume revisions
  const prefix = candidateFirstName
    ? t('greeting', { candidateName: candidateFirstName })
    : ''
  const suffix = coachName ? t('coach_signature', { coachName }) : ''

  const noteDefaults = {
    ops: [
      {
        insert: prefix + t('no_show_message') + suffix,
      },
    ],
  }

  const noteToCandidate = editorRef?.current?.getSemanticHTML()

  const resetForm = () => {
    setNoteDetails('')
    setPrivacySettings([])
    setIsSaving(false)
    setDuration('30')
  }

  const handleDialogOpenChange = (isOpen) => {
    setOpen(isOpen)
    if (!isOpen) {
      resetForm()
    }
  }

  const quillTemp = document.createElement('div')
  const quillInstance = new Quill(quillTemp)
  quillInstance.setContents(noteDefaults)

  const noDuration =
    duration === '' || duration === undefined || duration.length < 1

  const formIsInvalid =
    noteToCandidate?.toUpperCase().includes('COACH TO EDIT') ||
    noteToCandidate?.trim() === undefined ||
    noteToCandidate?.trim() === '' ||
    noDuration

  return (
    <Dialog
      open={open}
      onOpenChange={handleDialogOpenChange}
      className="rounded-2xl"
    >
      <DialogTrigger asChild className="cursor-pointer">
        <Button
          variant="link"
          className="m-0 justify-start p-0 font-normal text-intoo-blue-medium hover:text-link"
        >
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="font-roboto lg:max-w-3xl xl:max-w-5xl"
        variant="default"
        dialogCloseClass="text-black"
      >
        <DialogHeader>
          <DialogTitle className="text-left" asChild>
            <h2 className="text-2xl">New No-Show Entry</h2>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[80vh] overflow-y-auto px-4 pb-4">
          <DialogDescription className="p-0 text-lg">
            Mark this candidate as a no-show for the scheduled meeting.
          </DialogDescription>
          <div className="py-4">
            <Label htmlFor="duration" className="text-lg !font-medium">
              Duration (Minutes) (Required)
            </Label>
            <DurationSelect duration={duration} onValueChange={setDuration} />
          </div>
          <div className="pb-4">
            <Label htmlFor="noteDetails" className="text-lg !font-medium">
              Internal System Admin Notes
            </Label>
            <PrivacySettings
              setPrivacySettings={setPrivacySettings}
              privacySettings={privacySettings}
            />
            <Textarea
              className="min-h-56 w-full"
              name="noteDetails"
              value={noteDetails}
              onChange={(e) => setNoteDetails(e.target.value)}
            />
          </div>
          <hr className="border-t-2 border-gray-300" />
          <div className="py-4">
            <Label htmlFor="noteToCandidate" className="text-lg !font-medium">
              Please record any remarks with this update. Comments will be
              viewed by the candidate.
            </Label>
            <RichTextEditor
              className="min-h-56 w-full"
              defaultValue={noteDefaults}
              ref={editorRef}
            />
          </div>

          <div className="flex flex-row justify-end">
            <Button
              className="inline"
              onClick={saveClick}
              disabled={isSaving || formIsInvalid}
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default NoShowDialog
